import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import MediaPlayerProvider from '@/components/app/contentPlayer/MediaPlayerProvider';
import PacksProvider from '@/components/app/layout/PacksProvider';
import AudioPlayer from '@/components/emotion-detector/AudioPlayer';
import {
	CameraLayout,
	EmotionCapture,
	EmotionCardCarousel,
	EmotionChat,
	EmotionDetectorFooter,
	EmotionDetectorHeader,
	EmotionIntro,
	EmotionRecommendations,
	EmotionSelection,
	MainContent,
} from '@/components/emotion-detector/components';
import messages from '@/components/emotion-detector/messages';
import {
	useAudioCleanup,
	useAudioRef,
	useAutoRevealSelection,
	useDetectionReset,
	useEmotionCards,
	useEmotionChat,
	useEmotionDetection,
	useEmotionRecommendations,
} from '@/hooks/emotion-detector';
import { TopEmotion } from '@/utils/emotion-detector';

import { HeroDescription, HeroSection, HeroTitle } from './styles';

export default function CameraScreen() {
	const { formatMessage } = useIntl();

	// Initialize all hooks
	const emotionDetection = useEmotionDetection();
	const emotionChat = useEmotionChat(emotionDetection.journalId);
	const emotionRecommendations = useEmotionRecommendations(emotionDetection.emotion);
	const cardState = useEmotionCards();

	// Derive stable boolean values from Set state to avoid infinite loops
	const captureCardVisible = useMemo(
		() => cardState.visibleCards.has('capture'),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cardState.visibleCardsVersion],
	);

	// Track current audio element for cleanup
	const currentAudioRef = useAudioRef(emotionRecommendations.currentAudio.element);

	// Reset other components when detection starts
	useDetectionReset({
		detecting: emotionDetection.detecting,
		resetChat: emotionChat.resetChat,
		resetCardsAfterCapture: cardState.resetCardsAfterCapture,
		currentAudio: emotionRecommendations.currentAudio,
		setCurrentAudio: emotionRecommendations.setCurrentAudio,
	});

	// Stop audio playback when navigating away
	useAudioCleanup({
		audioRef: currentAudioRef,
		currentAudio: emotionRecommendations.currentAudio,
		setCurrentAudio: emotionRecommendations.setCurrentAudio,
	});

	// Auto-reveal selection card when emotion is detected
	useAutoRevealSelection({
		needsConfirm: emotionDetection.needsConfirm,
		emotion: emotionDetection.emotion,
		captureCardVisible,
		addVisibleCardAndScroll: cardState.addVisibleCardAndScroll,
	});

	// Handle starting the capture process
	const handleStartCapture = () => {
		cardState.addVisibleCardAndScroll('capture');
	};

	// Handle emotion confirmation/selection
	const handleEmotionConfirmed = (emotion: TopEmotion) => {
		cardState.addVisibleCardAndScroll('recommendations');

		emotionChat.startChatWith(emotion).catch(() => {
			// Chat start failed, ignore silently
		});

		setTimeout(() => {
			cardState.revealChatAndRecommendations();
		}, 1000);
	};

	return (
		<PacksProvider>
			<MediaPlayerProvider>
				<CameraLayout>
					<EmotionDetectorHeader currentPage="camera" />

					<MainContent>
						<HeroSection>
							<HeroTitle>{formatMessage(messages.cameraHeroTitleStart)}</HeroTitle>
							<HeroDescription>{formatMessage(messages.cameraHeroDescription)}</HeroDescription>
						</HeroSection>

						<EmotionCardCarousel cardState={cardState}>
							<EmotionIntro
								isVisible={cardState.visibleCards.has('intro')}
								onStartCapture={handleStartCapture}
							/>

							<EmotionCapture
								emotionDetection={emotionDetection}
								isVisible={cardState.visibleCards.has('capture')}
							/>

							{(emotionDetection.needsConfirm || emotionDetection.emotion) && (
								<EmotionSelection
									emotionDetection={emotionDetection}
									isVisible={cardState.visibleCards.has('selection')}
									onEmotionConfirmed={handleEmotionConfirmed}
								/>
							)}

							{emotionDetection.emotion && cardState.visibleCards.has('recommendations') && (
								<EmotionRecommendations
									emotionRecommendations={emotionRecommendations}
									isVisible={cardState.visibleCards.has('recommendations')}
								/>
							)}

							{emotionChat.chatActive && cardState.visibleCards.has('chat') && (
								<EmotionChat emotionChat={emotionChat} isVisible={cardState.visibleCards.has('chat')} />
							)}
						</EmotionCardCarousel>
					</MainContent>

					<AudioPlayer
						currentAudio={emotionRecommendations.currentAudio}
						setCurrentAudio={emotionRecommendations.setCurrentAudio}
					/>

					<EmotionDetectorFooter />
				</CameraLayout>
			</MediaPlayerProvider>
		</PacksProvider>
	);
}
